import React, { FC, useMemo, useCallback, useLayoutEffect } from 'react';
import { CommentParentType } from 'src/models/comments/types';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';
import { Skeleton, Collapse } from 'src/common';
import { useTranslation } from 'react-i18next';
import { Comments } from 'src/shared';
import styles from './index.module.scss';
import { isArray } from 'lodash';
import { useMarkCommentAsRead } from 'src/models/comments/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { useCurrentTenderBox } from 'src/models/procurements/Tender/hooks';
import { IBoxWithComments } from 'src/models/procurements/Tender/types';
import { useActiveUsers } from 'src/models/users/hooks';
import { scrollToItem } from 'src/helpers/scrollToElement';
import { useTenderData } from 'src/models/procurements/Tender/hooks';
import { Translations } from 'src/lib/I18nService/types';

const { Panel } = Collapse;

const getBoxCommentIDs = (currentBoxId: string, boxesWithComments: readonly IBoxWithComments[]): string[] => {
  const currentBox = boxesWithComments.find(_ => _.id === currentBoxId);
  return !!currentBox ? currentBox.comments.filter(comment => !comment.isRead).map(comment => comment.id) : [];
};

export const TenderComments: FC = () => {
  const { data: procurement, loading: procurementLoading } = useTenderData();
  const { t } = useTranslation();
  const { t: tenderT } = useTranslation(Translations.procurements);
  const users = useActiveUsers();

  const isProcCommentFeature = useFeatureFlag(FeatureFlag.Interactions_ProcurementLevelComments);

  const [markCommentAsRead] = useMarkCommentAsRead();
  const currentBox = useCurrentTenderBox();

  useLayoutEffect(() => {
    if (currentBox) {
      setTimeout(() => scrollToItem(currentBox.id));
    }
  }, [currentBox]);

  const boxesComments = useMemo(() => (procurement && procurement.boxesWithComments) || [], [procurement]);

  const isCommented = !!boxesComments.length;
  const isLoading = procurementLoading;

  const showComment = useCallback(
    (key: string | string[]) => {
      if (key && isArray(key) && key.length > 0) {
        key.forEach(id => {
          const unreadCommentsIds = getBoxCommentIDs(id, boxesComments);
          if (!!unreadCommentsIds.length) {
            unreadCommentsIds.forEach(commentId => {
              markCommentAsRead(commentId);
            });
          }
        });
      }
    },
    [boxesComments, markCommentAsRead]
  );

  const isNewBoxComment = useMemo(() => {
    return procurement && currentBox && !procurement.boxesWithComments.find(box => box.id === currentBox.id);
  }, [procurement, currentBox]);

  const defaultActiveKey = useMemo(() => {
    const commentsParentProcId = (procurement && [procurement.id]) || [];
    const commentsParentBoxesIds = !!boxesComments.length ? boxesComments.map(box => box.id) : [];
    const newCommentParentBoxId = currentBox && isNewBoxComment ? [currentBox.id] : [];

    return [...commentsParentProcId, ...commentsParentBoxesIds, ...newCommentParentBoxId];
  }, [boxesComments, isNewBoxComment, procurement, currentBox]);

  return isLoading ? (
    <Skeleton loading active />
  ) : (
    <>
      <Collapse
        className={styles.commentsGroups}
        onChange={showComment}
        defaultActiveKey={defaultActiveKey}
        expandIcon={() => <FontAwesomeIcon icon={faChevronDown} className={styles.groupExpandIcon} />}
      >
        {!!procurement && isProcCommentFeature && (
          <Panel
            header={
              <div className={styles.groupHeader}>
                <span className={styles.groupTitle}>{t('DetailsPage.commentOnProcurement')}</span>
              </div>
            }
            key={procurement.id}
            className={styles.commentsGroup}
            id={procurement.id}
          >
            <Comments
              comments={procurement.comments}
              parentId={procurement.id}
              parentType={CommentParentType.Procurement}
              mode={'multiple'}
              users={users}
            />
          </Panel>
        )}
        {isCommented &&
          boxesComments.map(box => (
            <Panel
              header={
                <div className={styles.groupHeader}>
                  <span className={styles.groupTitle}>{tenderT(box.title)}</span>
                  {box.questionId ? <span className={styles.groupSubTitle}>ID: {box.questionId}</span> : null}
                </div>
              }
              key={box.id}
              className={styles.commentsGroup}
              id={box.id}
            >
              <Comments
                key={`commentList_${box.title}`}
                comments={box.comments}
                parentId={box.id}
                parentType={CommentParentType.ProcurementBox}
                mode={'multiple'}
                users={users}
              />
            </Panel>
          ))}
        {isNewBoxComment && currentBox && (
          <Panel
            header={
              <div className={styles.groupHeader}>
                <span className={styles.groupTitle}>{tenderT(currentBox.title)}</span>
                {currentBox.questionId ? (
                  <span className={styles.groupSubTitle}>ID: {currentBox.questionId}</span>
                ) : null}
              </div>
            }
            key={currentBox.id}
            className={styles.commentsGroup}
            id={currentBox.id}
          >
            <Comments
              comments={[]}
              parentId={currentBox.id}
              parentType={CommentParentType.ProcurementBox}
              mode={'multiple'}
              users={users}
            />
          </Panel>
        )}
      </Collapse>
    </>
  );
};

export default TenderComments;
